import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TaskFilterTypes } from '@app/enums';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { Employee } from '@app/models/employee/employee.model';
import { Task } from '@app/models/tasks/task.model';
import { DeprecatedTaskChecklistComponent } from '@app/modules/employees/components/task-checklist/deprecated/deprecated-task-checklist.component';
import { AuthService } from '@app/services/auth.service';

const RESULTS_PER_PAGE = 25;
const isScrollContainerAtBottom = (element: HTMLElement): boolean => {
    const itemHeight = element.querySelector('.task').clientHeight;
    return element.scrollTop + element.offsetHeight >= element.scrollHeight - itemHeight;
};
@Component({
    styleUrls: ['./completed-tasks.style.scss'],
    templateUrl: './completed-tasks.view.html',
})
export class CompletedTasksView implements OnInit {
    @ViewChild('taskChecklist', { static: false }) taskChecklist: DeprecatedTaskChecklistComponent;

    isAdmin = false;
    incompleteTasksCount = 0;

    title = 'components.completed-tasks.myCompletedTasks';

    employee: Employee;
    tasks = [];
    taskFilterType = TaskFilterTypes.myself;
    currentTaskPage = 0;
    totalTasks = 0;
    isLoadingTasks = false;
    shouldConcat = false;

    constructor(
        public auth: AuthService,
        private router: Router
    ) {}

    async ngOnInit(): Promise<void> {
        this.isLoadingTasks = true;
        if (this.auth.isAdmin()) {
            this.isAdmin = true;
        }

        this.employee = this.auth.employee;
        await this.getCompletedTasks();
        this.isLoadingTasks = false;
    }

    navigateToAllTasksView(): void {
        this.router.navigate(['/employees/tasks']);
    }

    async onToggleTaskFilter(taskFilterType: TaskFilterTypes): Promise<void> {
        this.isLoadingTasks = true;
        this.currentTaskPage = 0;
        this.taskFilterType = taskFilterType;
        await this.getCompletedTasks();
        this.isLoadingTasks = false;
    }

    async updateTasks(): Promise<void> {
        this.isLoadingTasks = true;
        this.currentTaskPage = 0;
        this.shouldConcat = false;

        await this.getCompletedTasks();
        this.isLoadingTasks = false;
    }

    async onScrollTasksList(): Promise<void> {
        const element = this.taskChecklist.container.nativeElement;
        const shouldLoadMoreTasks =
            this.tasks.length &&
            !this.isLoadingTasks &&
            isScrollContainerAtBottom(element) &&
            this.currentTaskPage * RESULTS_PER_PAGE <= this.totalTasks;

        if (!shouldLoadMoreTasks) {
            return;
        }

        this.shouldConcat = true;
        this.isLoadingTasks = true;
        await this.getCompletedTasks();
        this.isLoadingTasks = false;
    }

    private async getCompletedTasks(): Promise<void> {
        this.isLoadingTasks = true;
        const query = this.baseQuery().clone();

        const [tasks, meta] = await query
            .where('isComplete', true)
            .when(this.taskFilterType === TaskFilterTypes.myself, ($query) =>
                $query.where('assigneeId', this.employee.id)
            )
            .page(this.currentTaskPage + 1)
            .get();

        this.currentTaskPage = meta.pagination.page;
        this.totalTasks = meta.pagination.total;

        this.tasks = this.shouldConcat ? [...this.tasks, ...tasks] : tasks;
        this.isLoadingTasks = false;
    }

    private baseQuery(): QueryFetcher {
        return Task.param('employee', this.auth.employee.id)
            .where('excludeTerminatedSubjects', true)
            .with([
                'assignee',
                'file',
                'subject',
                'subject.manager',
                'employeeDocument',
                'completedBy',
                'subtasksCount',
                'subtasks',
                'assignees',
                'assignees.manager',
            ]);
    }
}
